import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["input", "dropdown", "hiddenInput", "clearButton"]
  static values = { searchUrl: String }

  connect() {
    this.debounceTimer = null
    this.selectedIndex = -1
    this.results = []
    this.toggleClear()
    
    document.addEventListener('click', this.hideDropdown.bind(this))
  }

  disconnect() {
    document.removeEventListener('click', this.hideDropdown.bind(this))
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  blockSubmit(event) {
    if (event.key === 'Enter') event.preventDefault()
  }

  search(event) {
    const query = event.target.value.trim()
    
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.navigateDown()
      return
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.navigateUp()
      return
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (this.selectedIndex >= 0) {
        this.selectCurrent()
      } else if (!query) {
        this.clear()
      }
      return
    } else if (event.key === 'Escape') {
      this.hideDropdown()
      this.inputTarget.blur()
      return
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    if (query.length < 2) {
      this.hideDropdown()
      this.clearHiddenInput()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.append('q', query)
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`)
      }
      
      const data = await response.json()
      this.displayResults(data)
      
    } catch (error) {
      if (error.name === 'AbortError') return

      console.error('Hollander search error:', error.message)
      this.showError((window.buildSheetStrings || {}).search_unavailable || 'Search unavailable')
    }
  }

  displayResults(results) {
    this.results = results
    this.selectedIndex = results.length > 0 ? 0 : -1
    
    const dropdown = this.dropdownTarget
    
    if (results.length === 0) {
      dropdown.innerHTML = `<div class="hollander-empty">${(window.buildSheetStrings || {}).no_parts || 'No parts found'}</div>`
    } else {
      dropdown.innerHTML = results.map((result, index) => `
        <div class="hollander-result ${index === this.selectedIndex ? 'selected' : ''}" 
             data-index="${index}" 
             data-hollander="${result.hollander}"
             data-action="click->hollander-search#select">
          <span class="hollander-code">${result.hollander}</span>
          <span class="hollander-description">${result.plain_english_name}</span>
        </div>
      `).join('')
    }
    
    this.showDropdown()
  }

  showError(message) {
    const dropdown = this.dropdownTarget
    dropdown.innerHTML = `<div class="hollander-empty">⚠️ ${message}</div>`
    this.showDropdown()
  }

  select(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (this.results[index]) {
      this.selectResult(this.results[index])
    }
  }

  selectResult(result) {
    this.inputTarget.value = result.plain_english_name
    
    this.hiddenInputTarget.value = result.hollander
    this.toggleClear()
    
    this.hideDropdown()
    
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    
    this.autoSubmitForm()
  }
  
  autoSubmitForm() {
    const form = this.element.closest('form')
    if (form && this.hiddenInputTarget.value) {
      setTimeout(() => {
        const searchesController = form.controller
        if (searchesController && searchesController.submit) {
          searchesController.submit()
        } else {
          const baseUrl = form.dataset.url
          const hollanderCode = this.hiddenInputTarget.value
          Turbo.visit(`${baseUrl}?query=${hollanderCode}`)
        }
      }, 100)
    }
  }

  selectCurrent() {
    if (this.selectedIndex >= 0 && this.results[this.selectedIndex]) {
      this.selectResult(this.results[this.selectedIndex])
    }
  }

  navigateDown() {
    if (this.selectedIndex < this.results.length - 1) {
      this.selectedIndex++
      this.updateSelection()
    }
  }

  navigateUp() {
    if (this.selectedIndex > 0) {
      this.selectedIndex--
      this.updateSelection()
    } else if (this.selectedIndex === 0) {
      this.selectedIndex = -1
      this.updateSelection()
    }
  }

  updateSelection() {
    const items = this.dropdownTarget.querySelectorAll('.hollander-result')
    items.forEach((item, index) => {
      item.classList.toggle('selected', index === this.selectedIndex)
    })
    
    const selected = items[this.selectedIndex]
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' })
    }
  }

  showDropdown() {
    this.dropdownTarget.style.display = 'block'
  }

  hideDropdown(event) {
    if (event && this.element.contains(event.target)) {
      return
    }
    
    this.dropdownTarget.style.display = 'none'
    this.selectedIndex = -1
  }

  clearHiddenInput() {
    if (this.hiddenInputTarget.value && !this.inputTarget.value) {
      this.hiddenInputTarget.value = ''
    }
  }

  clear() {
    this.inputTarget.value = ''
    this.hiddenInputTarget.value = ''
    this.hideDropdown()
    this.toggleClear()

    const applied = new URLSearchParams(window.location.search).get('query')
    const form = this.element.closest('form')
    if (applied && form && form.dataset.url) {
      window.location.href = form.dataset.url
      return
    }
    this.inputTarget.focus()
  }

  toggleClear() {
    this.clearButtonTarget.classList.toggle('d-none', !this.inputTarget.value.trim())
  }

  submitCurrent(event) {
    event.preventDefault()

    if (this.selectedIndex >= 0) {
      this.selectCurrent()
      return
    }

    const query = this.inputTarget.value.trim()
    if (query.length >= 2) {
      this.performSearch(query)
    } else {
      this.inputTarget.focus()
    }
  }
}